import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes } from '../Common';
import { Textarea, toasty } from '../common/forms/FormElements';
import ValidatedSelect from '../common/forms/ValidatedSelect';

// Description of the seeded TimesheetRejectionReason row used to mark a rejection
// as linked to an existing flag (see migration 20260525145328).
const FLAG_REASON_LABEL = 'Timesheet Flag';

const rejectionDestinationOptions = [
    { value: 'flagger', label: 'Reject to Flagger' },
    { value: 'operations', label: 'Reject to Operations' },
];

const RejectTimesheetModalBody = ({ timesheetId, values, allowDestination = false }) => {
    // Active (non-dismissed) flags, already shaped into dropdown options via select.
    const flagsQuery = useQuery({
        queryKey: ['timesheetFlags', timesheetId],
        queryFn: () => httpClient.js(ApiRoutes.timesheetManagement.flags(timesheetId)),
        select: (data) => (data || [])
            .filter((f) => !f.isDismissed)
            .map((f) => ({
                value: f.id,
                label: `${f.flagType} – ${f.category}${f.employeeName ? ` (${f.employeeName})` : ''}`,
            })),
    });

    const reasonsQuery = useQuery({
        queryKey: ['rejectionReasons'],
        queryFn: () => httpClient.js(ApiRoutes.typeAheads.rejectionReasons()),
        select: (data) => (data || []).map((r) => ({ value: r.value, label: r.label })),
    });

    useEffect(() => {
        if (flagsQuery.isError || reasonsQuery.isError) {
            toasty.error('Failed to load rejection options.');
        }
    }, [flagsQuery.isError, reasonsQuery.isError]);

    if (flagsQuery.isLoading || reasonsQuery.isLoading) {
        return <div className="text-muted">Loading…</div>;
    }

    const flagOptions = flagsQuery.data || [];
    const allReasons = reasonsQuery.data || [];
    const flagReasonValue = allReasons.find((r) => r.label === FLAG_REASON_LABEL)?.value;
    // Only offer the "Timesheet Flag" reason when there are active flags to link to.
    const reasonOptions = flagOptions.length > 0
        ? allReasons
        : allReasons.filter((r) => r.label !== FLAG_REASON_LABEL);

    const flagSelected = flagReasonValue != null
        && Number(values?.rejectionReason) === Number(flagReasonValue);

    return (
        <>
            {allowDestination && (
                <ValidatedSelect
                    label="Destination"
                    id="rejectionDestination"
                    name="rejectionDestination"
                    required
                    error="A destination is required."
                    asPrimitive
                    hideClear
                    options={rejectionDestinationOptions}
                />
            )}

            <ValidatedSelect
                label="Reason"
                id="rejectionReason"
                name="rejectionReason"
                required
                error="A reason is required."
                asPrimitive
                hideClear
                options={reasonOptions}
            />

            {flagSelected && (
                <ValidatedSelect
                    label="Flag"
                    id="linkedFlagId"
                    name="linkedFlagId"
                    required
                    error="A flag is required."
                    asPrimitive
                    hideClear
                    options={flagOptions}
                />
            )}

            <Textarea
                label="Notes"
                id="rejectionNotes"
                name="rejectionNotes"
                required
                placeholder="Enter notes regarding the rejection."
                maxLength="500"
                rows="5"
                error="Notes are required."
            />
        </>
    );
};

export default RejectTimesheetModalBody;
